import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { trainingApi } from "../../services/trainingApi";
import { usePermissions } from "../../hooks/usePermissions";
import { StatusBadge } from "../../components/StatusBadge";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { formatApiErrorDetail } from "../../lib/api";
import { useLocalization } from "../../hooks/useLocalization";

export default function TrainingCoursesPage() {
  const navigate = useNavigate();
  const { t } = useLocalization();
  const { canViewTraining, canManageTrainingContent } = usePermissions();
  const [courses, setCourses] = useState([]);
  const [form, setForm] = useState({ key: "", title_en: "", title_ja: "", pass_score: 90, has_practical_requirement: false });

  const load = () => trainingApi.listCourses().then((r) => setCourses(r.data)).catch(() => {});

  useEffect(() => { if (canViewTraining) load(); }, [canViewTraining]);

  if (!canViewTraining) return <p className="text-sm text-gray-500" data-testid="training-courses-access-denied">{t("common.accessDenied")}</p>;

  const createCourse = async () => {
    if (!form.key || !form.title_en) { toast.error(t("mgmtTraining.courseKeyTitleRequired")); return; }
    try {
      const { data } = await trainingApi.createCourse(form);
      toast.success(t("mgmtTraining.courseCreated"));
      setForm({ key: "", title_en: "", title_ja: "", pass_score: 90, has_practical_requirement: false });
      load();
      navigate(`/management/training/courses/${data.id}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="training-courses-page">
      <PageHelpBanner pageKey="training-courses" title={t("pageHelp.defaultTitle")}>
        <p dangerouslySetInnerHTML={{ __html: t("mgmtTraining.coursesHelp") }} />
      </PageHelpBanner>
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("mgmtTraining.courses")}</h2>
        <p className="text-sm text-gray-500">{t("mgmtTraining.coursesSubtitle")}</p>
      </div>

      {canManageTrainingContent && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="create-course-form">
          <input data-testid="course-key-input" value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value.toUpperCase().replace(/\s/g, "_") })} placeholder={t("mgmtTraining.courseKeyPlaceholder")} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm" />
          <input data-testid="course-title-en-input" value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} placeholder={t("mgmtTraining.titleEnPlaceholder")} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm" />
          <input data-testid="course-title-ja-input" value={form.title_ja} onChange={(e) => setForm({ ...form, title_ja: e.target.value })} placeholder={t("mgmtTraining.titleJaPlaceholder")} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm" />
          <input data-testid="course-pass-score-input" type="number" value={form.pass_score} onChange={(e) => setForm({ ...form, pass_score: Number(e.target.value) })} placeholder={t("mgmtTraining.passScorePlaceholder")} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm" />
          <button data-testid="create-course-btn" onClick={createCourse} className="px-4 py-2 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700">{t("mgmtTraining.createCourse")}</button>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-gray-50 text-gray-500 text-left"><tr><th className="px-4 py-3 font-medium">{t("mgmtLeavePolicies.colKey")}</th><th className="px-4 py-3 font-medium">{t("mgmtLeavePolicies.colTitle")}</th><th className="px-4 py-3 font-medium">{t("common.status")}</th><th className="px-4 py-3 font-medium"></th></tr></thead>
          <tbody>
            {courses.map((c) => (
              <tr key={c.id} className="border-t border-gray-100" data-testid={`course-row-${c.id}`}>
                <td className="px-4 py-2 font-semibold text-gray-800">{c.key}</td>
                <td className="px-4 py-2 text-gray-600">{c.title_en}</td>
                <td className="px-4 py-2"><StatusBadge status={c.status} /></td>
                <td className="px-4 py-2"><button data-testid={`open-course-btn-${c.id}`} onClick={() => navigate(`/management/training/courses/${c.id}`)} className="text-xs font-semibold text-blue-600 hover:underline">{t("mgmtWorkCalendar.openArrow")}</button></td>
              </tr>
            ))}
            {courses.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-500" data-testid="no-courses-row">{t("mgmtTraining.noCourses")}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
